<template>
  <div v-loading="isUploading" class="voice-input-page">
    <!-- 顶部状态栏 -->
    <div class="header-section">
      <div class="step-label">语音录入</div>
      <div class="divider" />
      <div class="status-text">{{ statusText }}</div>
    </div>

    <!-- 录音按钮核心区 -->
    <div class="mic-section">
      <div class="mic-wrapper" @click="toggleRecording">
        <div class="mic-ring" :class="{ active: isRecording }" />
        <div class="mic-ring ring-2" :class="{ active: isRecording }" />
        <div class="mic-btn" :class="{ recording: isRecording }">
          <i :class="isRecording ? 'el-icon-video-pause' : 'el-icon-microphone'" />
        </div>
      </div>
      <div class="mic-hint">{{ isRecording ? '正在聆听，点击暂停' : '点击开始语音输入' }}</div>
    </div>

    <!-- 文字展示区域 -->
    <div class="text-panel">
      <div v-if="interimResult" class="text-line interim">{{ interimResult }}</div>
      <div v-if="finalResult" class="text-line final">{{ finalResult }}</div>
      <div v-if="!interimResult && !finalResult" class="placeholder">
        <i class="el-icon-chat-line-round" />
        <span>语音内容将实时显示在这里</span>
      </div>
    </div>

    <!-- 底部操作按钮 -->
    <div class="footer-actions">
      <div class="action-btn btn-cancel" :class="{ disabled: isUploading }" @click="handleCancel">取消</div>
      <div class="action-btn btn-confirm" :class="{ disabled: isUploading }" @click="handleConfirm">确定</div>
    </div>
  </div>
</template>

<script>
import { postFileData } from '@/api/ik/data/dataApi'

export default {
  name: 'VoiceInputPage',
  props: {
    // 外部传入的 Token（优先级高于自动获取）
    speechToken: { type: String, default: '' },
    speechAppKey: { type: String, default: 'bYgC41rWqjKoRXb3' },
    speechWebSocketUrl: { type: String, default: 'wss://nls-gateway.cn-shanghai.aliyuncs.com/ws/v1' },
    accessKeyId: { type: String, default: process.env.VUE_APP_ALIYUN_ACCESS_KEY_ID },
    accessKeySecret: { type: String, default: process.env.VUE_APP_ALIYUN_ACCESS_KEY_SECRET },
    autoFetchToken: { type: Boolean, default: true }
  },
  data() {
    return {
      // 录音状态
      isRecording: false,
      speechStatus: '未连接',
      interimResult: '',
      finalResult: '',
      fullText: '', // 累计的完整识别结果

      // WebSocket & 音频（实时识别）
      websocket: null,
      audioContext: null,
      scriptProcessor: null,
      audioInput: null,
      audioStream: null,
      speechTaskId: '',
      hasStartedTranscription: false,

      // Token
      currentToken: '',
      tokenStatus: '空闲',
      tokenRefreshTimer: null,

      // 音频文件录制相关
      mediaRecorder: null,
      audioChunks: [],
      audioBlob: null,
      audioFile: null,
      isUploading: false
    }
  },
  computed: {
    statusText() {
      if (this.isUploading) return '正在提交...'
      if (this.isRecording) return '录音中 · 实时转写'
      if (this.speechStatus === '已连接') return '已连接 · 等待录音'
      if (this.speechStatus === '连接中...') return '正在连接服务...'
      return '点击麦克风开始语音输入'
    }
  },
  created() {
    this.initToken()
  },
  beforeDestroy() {
    this.stopRecording()
    this.disconnectWebSocket()
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer)
  },
  methods: {
    // ========== Token 管理（复用 aiDialog） ==========
    async initToken() {
      if (this.speechToken) {
        this.currentToken = this.speechToken
        this.tokenStatus = '已获取(外部传入)'
        return
      }
      if (this.autoFetchToken && this.accessKeyId && this.accessKeySecret) {
        await this.fetchToken()
      } else {
        this.tokenStatus = '失败(缺少AK配置)'
        console.warn('未配置 AccessKey，无法自动获取 Token')
      }
    },

    async fetchToken() {
      if (!this.accessKeyId || !this.accessKeySecret) {
        this.$message.error('缺少 AccessKey 配置，无法获取 Token')
        this.tokenStatus = '失败(缺少AK配置)'
        return
      }
      this.tokenStatus = '获取中'
      try {
        const params = {
          AccessKeyId: this.accessKeyId,
          Action: 'CreateToken',
          Version: '2019-02-28',
          Format: 'JSON',
          RegionId: 'cn-shanghai',
          SignatureMethod: 'HMAC-SHA1',
          SignatureVersion: '1.0',
          SignatureNonce: this.generateUUID(),
          Timestamp: this.getISO8601Time()
        }
        const canonicalizedQuery = this.canonicalizedQuery(params)
        const stringToSign = `GET&${this.percentEncode('/')}&${this.percentEncode(canonicalizedQuery)}`
        const signature = await this.sign(stringToSign, this.accessKeySecret + '&')
        const queryString = `Signature=${signature}&${canonicalizedQuery}`
        const url = `https://nls-meta.cn-shanghai.aliyuncs.com/?${queryString}`

        const response = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }})
        const data = await response.json()

        if (data.Token) {
          this.currentToken = data.Token.Id || data.Token
          this.tokenStatus = '已获取'
          const expireTime = data.Token.ExpireTime || data.ExpireTime
          const refreshDelay = Math.max((expireTime - Date.now() / 1000 - 300) * 1000, 60000)
          if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer)
          this.tokenRefreshTimer = setTimeout(() => this.fetchToken(), refreshDelay)
        } else {
          throw new Error(data.Message || '获取Token失败')
        }
      } catch (error) {
        console.error('获取Token失败:', error)
        this.tokenStatus = '失败'
      }
    },

    percentEncode(str) {
      if (!str) return ''
      return encodeURIComponent(str).replace(/\+/g, '%20').replace(/\*/g, '%2A').replace(/%7E/g, '~')
    },

    canonicalizedQuery(params) {
      return Object.keys(params).sort().map(key => {
        return `${this.percentEncode(key)}=${this.percentEncode(params[key])}`
      }).join('&')
    },

    async sign(stringToSign, secret) {
      const encoder = new TextEncoder()
      const keyData = encoder.encode(secret)
      const messageData = encoder.encode(stringToSign)
      const cryptoKey = await crypto.subtle.importKey('raw', keyData, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign'])
      const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData)
      const base64 = btoa(String.fromCharCode(...new Uint8Array(signature)))
      return this.percentEncode(base64)
    },

    getISO8601Time() {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      return `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}T${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(now.getUTCSeconds())}Z`
    },

    generateUUID() {
      const hex = '0123456789abcdef'
      let uuid = ''
      for (let i = 0; i < 32; i++) uuid += hex[Math.floor(Math.random() * 16)]
      return uuid
    },

    // ========== 录音控制 ==========
    async toggleRecording() {
      if (this.isRecording) {
        this.stopRecording()
      } else {
        if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
          try {
            await this.connectWebSocket()
          } catch (error) {
            this.$message.error('语音识别服务连接失败')
            return
          }
        }
        this.startRecording()
      }
    },

    connectWebSocket() {
      return new Promise((resolve, reject) => {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          this.speechStatus = '已连接'
          resolve()
          return
        }
        const token = this.currentToken || this.speechToken
        if (!token) {
          reject(new Error('Token为空'))
          return
        }
        const appkey = this.speechAppKey
        const socketUrl = `${this.speechWebSocketUrl}?token=${token}&appkey=${appkey}`

        try {
          this.websocket = new WebSocket(socketUrl)
          this.speechStatus = '连接中...'

          const timeout = setTimeout(() => {
            if (this.websocket && this.websocket.readyState !== WebSocket.OPEN) {
              this.websocket.close()
              this.websocket = null
              this.speechStatus = '连接超时'
              reject(new Error('连接超时'))
            }
          }, 5000)

          this.websocket.onopen = () => {
            clearTimeout(timeout)
            this.speechStatus = '已连接'
            resolve()
          }

          this.websocket.onerror = (error) => {
            clearTimeout(timeout)
            this.speechStatus = '连接错误'
            reject(error)
          }

          this.websocket.onclose = () => {
            this.speechStatus = '未连接'
            this.isRecording = false
            this.hasStartedTranscription = false
            this.websocket = null
          }

          this.websocket.onmessage = (event) => {
            try {
              const message = JSON.parse(event.data)
              const headerName = message.header?.name

              if (headerName === 'TranscriptionStarted') {
                console.log('语音识别任务已启动')
              } else if (headerName === 'TranscriptionResultChanged') {
                this.interimResult = message.payload.result
              } else if (headerName === 'SentenceEnd') {
                const result = message.payload.result
                if (result) {
                  this.fullText += (this.fullText ? ' ' : '') + result
                  this.finalResult = this.fullText
                  this.interimResult = ''
                }
              } else if (headerName === 'TranscriptionCompleted') {
                this.hasStartedTranscription = false
              }
            } catch (e) {
              console.error('解析消息失败:', e)
            }
          }
        } catch (error) {
          this.speechStatus = '未连接'
          reject(error)
        }
      })
    },

    async startRecording() {
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) return
      if (this.isRecording) return

      try {
        if (!this.hasStartedTranscription) {
          this.sendStartTranscription()
          await new Promise(resolve => setTimeout(resolve, 300))
        }

        this.audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true
          }
        })

        // 同步启动 MediaRecorder 录制音频文件
        this.audioChunks = []
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4')

        this.mediaRecorder = new MediaRecorder(this.audioStream, { mimeType })
        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            this.audioChunks.push(event.data)
          }
        }
        this.mediaRecorder.onstop = () => {
          this.audioBlob = new Blob(this.audioChunks, { type: mimeType })
          const ext = mimeType.includes('webm') ? 'webm' : (mimeType.includes('mp4') ? 'mp4' : 'ogg')
          const filename = `voice_${this.formatDate(new Date())}.${ext}`
          this.audioFile = new File([this.audioBlob], filename, { type: mimeType })
        }
        this.mediaRecorder.start(100)

        // AudioContext 实时处理用于阿里云识别
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 })
        this.audioInput = this.audioContext.createMediaStreamSource(this.audioStream)
        this.scriptProcessor = this.audioContext.createScriptProcessor(2048, 1, 1)

        this.scriptProcessor.onaudioprocess = (event) => {
          if (!this.isRecording || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) return
          const inputData = event.inputBuffer.getChannelData(0)
          const inputData16 = new Int16Array(inputData.length)
          for (let i = 0; i < inputData.length; ++i) {
            inputData16[i] = Math.max(-1, Math.min(1, inputData[i])) * 0x7FFF
          }
          this.websocket.send(inputData16.buffer)
        }

        this.audioInput.connect(this.scriptProcessor)
        this.scriptProcessor.connect(this.audioContext.destination)
        this.isRecording = true
        this.speechStatus = '录音中...'
      } catch (error) {
        console.error('录音失败:', error)
        this.$message.error('无法启动录音: ' + error.message)
        this.isRecording = false
      }
    },

    formatDate(date) {
      const pad = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
    },

    sendStartTranscription() {
      const appkey = this.speechAppKey
      this.speechTaskId = this.generateUUID()
      const msg = {
        header: {
          appkey: appkey,
          namespace: 'SpeechTranscriber',
          name: 'StartTranscription',
          task_id: this.speechTaskId,
          message_id: this.generateUUID()
        },
        payload: {
          format: 'pcm',
          sample_rate: 16000,
          enable_intermediate_result: true,
          enable_punctuation_prediction: true,
          enable_inverse_text_normalization: true
        }
      }
      this.websocket.send(JSON.stringify(msg))
      this.hasStartedTranscription = true
    },

    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop()
      }

      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect()
        this.scriptProcessor = null
      }
      if (this.audioInput) {
        this.audioInput.disconnect()
        this.audioInput = null
      }
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop())
        this.audioStream = null
      }
      if (this.audioContext) {
        this.audioContext.close()
        this.audioContext = null
      }

      this.isRecording = false
      this.interimResult = ''

      if (this.websocket && this.websocket.readyState === WebSocket.OPEN && this.hasStartedTranscription) {
        const stopMessage = {
          header: {
            message_id: this.generateUUID(),
            task_id: this.speechTaskId,
            namespace: 'SpeechTranscriber',
            name: 'StopTranscription',
            appkey: this.speechAppKey
          },
          payload: {}
        }
        try {
          this.websocket.send(JSON.stringify(stopMessage))
        } catch (e) {
          console.error('发送停止消息失败:', e)
        }
        this.speechStatus = '已连接'
      }
      this.disconnectWebSocket()
    },

    disconnectWebSocket() {
      if (this.websocket) {
        this.websocket.close()
        this.websocket = null
      }
      this.speechStatus = '未连接'
      this.isRecording = false
      this.hasStartedTranscription = false
    },

    // ========== 底部按钮 ==========
    handleCancel() {
      if (this.isUploading) return
      this.stopRecording()
      this.fullText = ''
      this.finalResult = ''
      this.interimResult = ''
      this.audioBlob = null
      this.audioFile = null
      this.audioChunks = []
      this.$emit('cancel')
    },

    // ========== 确定按钮 - 上传音频文件 + 文字 ==========
    async handleConfirm() {
      if (this.isUploading) return
      this.stopRecording()

      // 等待 MediaRecorder 停止并生成 File
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        await new Promise(resolve => {
          const check = setInterval(() => {
            if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
              clearInterval(check)
              resolve()
            }
          }, 50)
        })
      }
      await new Promise(resolve => setTimeout(resolve, 200))

      const textResult = this.finalResult || this.fullText || ''
      if (!textResult.trim() && !this.audioFile) {
        this.$message.warning('没有识别到语音内容，也未录制到音频')
        return
      }

      this.isUploading = true

      try {
        const formData = new FormData()

        // file 字段：音频文件
        if (this.audioFile) {
          formData.append('file', this.audioFile)
        } else {
          const emptyBlob = new Blob([], { type: 'audio/webm' })
          formData.append('file', new File([emptyBlob], 'empty.webm', { type: 'audio/webm' }))
        }

        // data 字段：用 Blob 包装并显式声明 charset=utf-8
        // storeCode 从路由 query 传值获取（路由传值），其次回退到 Vuex store
        const storeCode = this.$route?.query?.storeCode ||
                          this.$store?.state?.user?.storeCode ||
                          this.$store?.getters?.['user/getStoreCode'] || ''
        const dataObj = {
          data: {
            chiefComplaint: textResult,
            storeCode
          }
        }
        const dataBlob = new Blob(
          [JSON.stringify(dataObj)],
          { type: 'application/json; charset=utf-8' }
        )
        formData.append('data', dataBlob)

        // 使用项目统一的 API 方法上传
        const res = await postFileData(
          'clinic/treatment/treatment/diagrecord/do/checkchiefComplaint',
          formData
        )

        // 兼容两种响应结构：axios 原始响应 vs 拦截器已解包
        const responseBody = (res && res.data !== undefined) ? res.data : res

        // 检查业务状态码
        if (responseBody && responseBody.code !== undefined && responseBody.code !== 1) {
          this.$message.error(responseBody.message || '提交失败')
          return
        }

        // 提取业务数据
        const responseData = (responseBody && responseBody.data !== undefined)
          ? responseBody.data
          : (responseBody || {})

        // ==================== matchResult 特殊处理 ====================
        const matchResult = String(responseData?.matchResult || '')

        // matchResult = 2：描述过于简单，无法判断
        if (matchResult === '2') {
          this.$message.warning('症状描述过于简单，无法判断。请详细描述您的具体不适，例如疼痛部位、性质、持续时间、诱发因素等。')
          return
        }

        // matchResult = 3：未描述病情，属于闲聊
        if (matchResult === '3') {
          this.$message.warning('请描述您的病情症状，以便为您提供准确的诊断建议。')
          return
        }
        // ===========================================================

        // 提取下游页面需要的关键诊断字段
        const diagnosisPayload = {
          text: textResult,
          responseData: responseBody,
          chDiagDictionaryCode: responseData?.chDiagDictionary?.code,
          chDiagName: responseData?.chDiagName,
          matchResult: responseData?.matchResult,
          asrText: responseData?.asrText,
          analysis: responseData?.analysis,
          chiefComplaint: responseData?.chiefComplaint
        }

        // 正常情况必须有诊断编码才能进入症状选择
        if (!diagnosisPayload.chDiagDictionaryCode) {
          this.$message.warning('未获取到诊断编码，无法进入症状选择，请检查接口返回')
          console.error('接口返回数据:', responseBody)
          return
        }

        this.$message.success('提交成功')

        // 兼容旧监听方式，同时新增 complete 事件方便父组件直接接管
        this.$emit('confirm', textResult, responseBody)
        this.$emit('complete', diagnosisPayload)

        // 只有成功进入下一步时才清空本地数据
        this.fullText = ''
        this.finalResult = ''
        this.interimResult = ''
        this.audioBlob = null
        this.audioFile = null
        this.audioChunks = []
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error('提交失败: ' + (error.message || '未知错误'))
      } finally {
        this.isUploading = false
        // matchResult=2/3 时保留数据，让用户补充描述后重新提交
      }
    }
  }
}
</script>

<style scoped lang="scss">
.voice-input-page {
  width: 100%;
  height: 100vh;
  min-height: 600px;
  background: #0a0e27;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px 32px;
  box-sizing: border-box;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* 顶部标题区 */
.header-section {
  width: 100%;
  max-width: 480px;
  margin-bottom: 40px;

  .step-label {
    font-size: 18px;
    color: #4a9eff;
    font-weight: 600;
    letter-spacing: 1px;
    margin-bottom: 12px;
  }

  .divider {
    width: 100%;
    height: 1px;
    background: rgba(255, 255, 255, 0.15);
    margin-bottom: 12px;
  }

  .status-text {
    font-size: 14px;
    color: #8b92b4;
    font-style: italic;
  }
}

/* 麦克风区域 */
.mic-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 36px;

  .mic-wrapper {
    position: relative;
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;

    .mic-ring {
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      border: 2px solid rgba(74, 158, 255, 0.3);
      opacity: 0;
      transform: scale(1);

      &.active {
        opacity: 1;
        animation: ripple 2s ease-out infinite;
      }

      &.ring-2 {
        &.active {
          animation-delay: 0.6s;
        }
      }
    }

    .mic-btn {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: linear-gradient(135deg, #1a5fb4 0%, #4a9eff 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 20px rgba(74, 158, 255, 0.4);
      transition: all 0.3s ease;
      z-index: 2;

      i {
        font-size: 32px;
        color: #ffffff;
      }

      &:active {
        transform: scale(0.95);
      }

      &.recording {
        background: linear-gradient(135deg, #c0392b 0%, #e74c3c 100%);
        box-shadow: 0 4px 20px rgba(231, 76, 60, 0.4);
        border-radius: 24px;

        i {
          font-size: 28px;
        }
      }
    }
  }

  .mic-hint {
    margin-top: 16px;
    font-size: 14px;
    color: #8b92b4;
    letter-spacing: 0.5px;
  }
}

/* 文字展示面板 */
.text-panel {
  width: 100%;
  max-width: 480px;
  flex: 1;
  min-height: 120px;
  max-height: 280px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
  margin-bottom: 32px;

  .text-line {
    font-size: 16px;
    line-height: 1.8;
    word-break: break-all;

    &.interim {
      color: #8b92b4;
      font-style: italic;
      border-left: 3px solid #4a9eff;
      padding-left: 12px;
      margin-bottom: 8px;
    }

    &.final {
      color: #ffffff;
    }
  }

  .placeholder {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #5a6380;
    font-size: 14px;
    gap: 8px;

    i {
      font-size: 24px;
      opacity: 0.6;
    }
  }

  /* 自定义滚动条 */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 2px;
  }
}

/* 底部按钮 */
.footer-actions {
  width: 100%;
  max-width: 480px;
  display: flex;
  gap: 16px;

  .action-btn {
    flex: 1;
    height: 48px;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.25s ease;
    letter-spacing: 1px;

    &.btn-cancel {
      border: 1px solid rgba(255, 255, 255, 0.25);
      color: #ffffff;
      background: transparent;

      &:hover:not(.disabled) {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.4);
      }

      &:active:not(.disabled) {
        transform: scale(0.98);
      }

      &.disabled {
        opacity: 0.35;
        cursor: not-allowed;
        border-color: rgba(255, 255, 255, 0.12);
      }
    }

    &.btn-confirm {
      border: 1px solid #4a9eff;
      color: #4a9eff;
      background: rgba(74, 158, 255, 0.1);

      &:hover:not(.disabled) {
        background: rgba(74, 158, 255, 0.2);
        box-shadow: 0 0 16px rgba(74, 158, 255, 0.25);
      }

      &:active:not(.disabled) {
        transform: scale(0.98);
      }

      &.disabled {
        opacity: 0.35;
        cursor: not-allowed;
        border-color: rgba(74, 158, 255, 0.3);
        color: rgba(74, 158, 255, 0.5);
      }
    }
  }
}

/* 波纹动画 */
@keyframes ripple {
  0% {
    transform: scale(1);
    opacity: 0.6;
    border-color: rgba(74, 158, 255, 0.5);
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
    border-color: rgba(74, 158, 255, 0);
  }
}
</style>
